import React from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getWeightLengthRechartsScatterProps,
  sortSpeciesForWeightLengthLegend,
  tagIdFromSpeciesLabel,
} from "../colors";
import { formatTick, formatLengthTick as formatLengthTickUtil } from "../formatters";

function fishTypeLabelFromRow(d) {
  if (d.fishTypeName) return d.fishTypeName;
  const sp = d.species;
  if (
    sp &&
    !tagIdFromSpeciesLabel(sp) &&
    sp !== "Tagged Fish" &&
    sp !== "Untagged Fish"
  ) {
    return sp;
  }
  return null;
}

export default function WeightVsLengthChart({ graphData, selectedUnit, selectedFish, isMobile, hoveredWeightPoint, setHoveredWeightPoint }) {
  const formatLengthTick = (v) => formatLengthTickUtil(v, selectedUnit.value);
  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };
const uniqueSpeciesWeight = sortSpeciesForWeightLengthLegend([
  ...new Set(graphData.map((d) => d.species)),
]);
const fishTypesForStandard = sortSpeciesForWeightLengthLegend([
  ...new Set(graphData.map(fishTypeLabelFromRow).filter(Boolean)),
]);
// X (length): step from legacy padded span; left = max(0, dataMin - oneStep). Y: unchanged below.
const xValsW = graphData.map(d => Number(d.length || 0));
const yValsW = graphData.map(d => Number(Math.max(d.actualWeight || 0, d.standardWeight || 0)));

const isEnglishUnits = selectedUnit.value === 'English';
const targetTickCountWeight = 20;

let xStepW, xTicksW = [];
let yStepW, yTicksW = [];

// Calculate min and max values for x-axis
const xMinW = xValsW.length ? Math.min(...xValsW) : 0;
const xMaxW = xValsW.length ? Math.max(...xValsW) : 0;

if (isEnglishUnits) {
  const xEndW = xMaxW + 1;
  const rangeForStepW = xEndW - Math.max(0, Math.floor(xMinW) - 1);
  const xRangeW = rangeForStepW;

  // Determine step size based on the actual range
  if (xRangeW < 4) {
    xStepW = 0.1;
  } else if (xRangeW < 8) {
    xStepW = 0.2;
  } else if (xRangeW < 12) {
    xStepW = 0.3;
  } else if (xRangeW < 16) {
    xStepW = 0.4;
  } else if (xRangeW < 20) {
    xStepW = 0.5;
  } else if (xRangeW < 24) {
    xStepW = 0.6;
  } else if (xRangeW < 28) {
    xStepW = 0.7;
  } else if (xRangeW < 32) {
    xStepW = 0.8;
  } else if (xRangeW < 36) {
    xStepW = 0.9;
  } else {
    xStepW = 1.0;
  }

  const xStartW = Math.max(0, xMinW - xStepW);

  // Generate ticks from xStartW to xEndW
  xTicksW = [];
  for (let v = xStartW; v <= xEndW + 1e-9; v += xStepW) {
    xTicksW.push(Number(v.toFixed(2)));
  }

  // If we don't have enough ticks, extend the range
  while (xTicksW.length < targetTickCountWeight) {
    const lastTick = xTicksW[xTicksW.length - 1];
    xTicksW.push(Number((lastTick + xStepW).toFixed(2)));
  }
} else {
  const xEndW = xMaxW + 100;
  const rangeForStepW = xEndW - Math.max(0, xMinW - 100);
  const xRangeW = rangeForStepW;

  // Determine step size based on the actual range
  if (xRangeW < 400) {
    xStepW = 10;
  } else if (xRangeW < 600) {
    xStepW = 15;
  } else if (xRangeW < 800) {
    xStepW = 20;
  } else if (xRangeW < 1000) {
    xStepW = 25;
  } else if (xRangeW < 1200) {
    xStepW = 30;
  } else if (xRangeW < 1400) {
    xStepW = 35;
  } else if (xRangeW < 1600) {
    xStepW = 40;
  } else if (xRangeW < 1800) {
    xStepW = 45;
  } else if (xRangeW < 2000) {
    xStepW = 50;
  } else if (xRangeW < 2200) {
    xStepW = 55;
  } else if (xRangeW < 2400) {
    xStepW = 60;
  } else if (xRangeW < 2600) {
    xStepW = 65;
  } else if (xRangeW < 2800) {
    xStepW = 70;
  } else if (xRangeW < 3000) {
    xStepW = 75;
  } else if (xRangeW < 3200) {
    xStepW = 80;
  } else if (xRangeW < 3400) {
    xStepW = 85;
  } else if (xRangeW < 3600) {
    xStepW = 90;
  } else if (xRangeW < 3800) {
    xStepW = 95;
  } else if (xRangeW < 4000) {
    xStepW = 100;
  } else {
    xStepW = 200;
  }

  const xStartW = Math.max(0, xMinW - xStepW);

  // Generate ticks from xStartW to xEndW
  xTicksW = [];
  for (let v = xStartW; v <= xEndW + 1e-9; v += xStepW) {
    xTicksW.push(Number(v.toFixed(0))); // No decimals for mm
  }

  // If we don't have enough ticks, extend the range
  while (xTicksW.length < targetTickCountWeight) {
    const lastTick = xTicksW[xTicksW.length - 1];
    xTicksW.push(Number(lastTick + xStepW));
  }
}

// --- Y Axis (Weight) ticks: fixed gaps based on units, but ensure 40 ticks ---
const yMaxW = yValsW.length ? Math.max(...yValsW) : 0;

if (isEnglishUnits) {
  // English units (pounds) - SAME LOGIC AS FIRST GRAPH X-AXIS
  if (yMaxW < 1.5) {
    yStepW = 0.05;
  } else if (yMaxW < 2) {
    yStepW = 0.1;
  } else if (yMaxW < 4) {
    yStepW = 0.2;
  } else if (yMaxW < 8) {
    yStepW = 0.4;
  } else if (yMaxW < 12) {
    yStepW = 0.6;
  } else if (yMaxW < 16) {
    yStepW = 0.8;
  } else if (yMaxW < 20) {
    yStepW = 1.0;
  } else if (yMaxW < 24) {
    yStepW = 1.2;
  } else if (yMaxW < 28) {
    yStepW = 1.4;
  } else if (yMaxW < 32) {
    yStepW = 1.6;
  } else if (yMaxW < 36) {
    yStepW = 1.8;
  } else {
    yStepW = 2.0;
  }

  const requiredRangeY = (targetTickCountWeight - 1) * yStepW;
  const actualRangeY = Math.max(yMaxW, requiredRangeY);
  const yUpperW = Math.ceil(actualRangeY / yStepW) * yStepW;

  yTicksW = [];
  for (let v = 0; v <= yUpperW + 1e-9; v += yStepW) {
    yTicksW.push(Number(v.toFixed(2)));
  }

  while (yTicksW.length < targetTickCountWeight) {
    const lastTick = yTicksW[yTicksW.length - 1];
    yTicksW.push(Number((lastTick + yStepW).toFixed(2)));
  }
} else {

  const range = yMaxW;
  if (range < 200) {
    yStepW = 10;
  }
  else if (range < 400) {
    yStepW = 20;
  } else if (range < 600) {
    yStepW = 30;
  } else if (range < 800) {
    yStepW = 40;
  } else if (range < 1000) {
    yStepW = 50;
  } else if (range < 1200) {
    yStepW = 60;
  } else if (range < 1400) {
    yStepW = 70;
  } else if (range < 1600) {
    yStepW = 80;
  } else if (range < 1800) {
    yStepW = 90;
  } else if (range < 2000) {
    yStepW = 100;
  } else if (range < 2200) {
    yStepW = 110;
  } else if (range < 2400) {
    yStepW = 120;
  } else if (range < 2600) {
    yStepW = 130;
  } else if (range < 2800) {
    yStepW = 140;
  } else if (range < 3000) {
    yStepW = 150;
  } else if (range < 3200) {
    yStepW = 160;
  } else if (range < 3400) {
    yStepW = 170;
  } else if (range < 3600) {
    yStepW = 180;
  } else if (range < 3800) {
    yStepW = 190;
  } else if (range < 4000) {
    yStepW = 200;
  } else {
    yStepW = 200;
  }

  const requiredRangeY = (targetTickCountWeight - 1) * yStepW;
  const actualRangeY = Math.max(yMaxW, requiredRangeY);
  const yUpperW = Math.ceil(actualRangeY / yStepW) * yStepW;

  yTicksW = [];
  for (let v = 0; v <= yUpperW + 1e-9; v += yStepW) {
    yTicksW.push(Number(v.toFixed(0))); // No decimals for grams
  }

  // If we still don't have enough ticks, add more - SAME AS FIRST GRAPH
  while (yTicksW.length < targetTickCountWeight) {
    const lastTick = yTicksW[yTicksW.length - 1];
    yTicksW.push(Number(lastTick + yStepW));
  }
}

return (
  <ResponsiveContainer {...commonProps}>
    <ComposedChart data={graphData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
      <XAxis
        dataKey="length"
        label={{
          value: `Length (${selectedUnit.value === "English" ? "Inch" : "mm"
            })`,
          position: "outsideBottom",
          offset: isMobile ? 10 : 20,
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        type="number"
        domain={[xTicksW[0] || 0, xTicksW[xTicksW.length - 1] || 0]}
        ticks={xTicksW}
        tickFormatter={formatLengthTick}
        angle={isMobile ? -45 : 0}
        textAnchor="middle"
        height={isMobile ? 80 : 60}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
        interval={0}
        allowDataOverflow={true}
        padding={{ right: 10 }}
      />
      <YAxis
        label={{
          value: `Weight (${selectedUnit.value === "English" ? "Pound" : "gram"
            })`,
          angle: -90,
          position: "insideLeft",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        domain={[0, yTicksW[yTicksW.length - 1] || 0]}
        ticks={yTicksW}
        tickFormatter={(value) => {
          if (selectedUnit.value === "English") {
            // English: show decimals as needed
            return formatTick(value);
          } else {
            // Metric: always show whole numbers only
            return Math.round(value).toString();
          }
        }}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
        interval={0}
      />
      <Tooltip
        filterNull
        isAnimationActive={false}
        cursor={{ stroke: 'transparent' }}
        content={({ active, payload }) => {
          // Only show tooltip when a scatter dot is actually hovered (set by onMouseEnter/onMouseLeave)
          if (!hoveredWeightPoint) return null;
          if (!active || !payload || !payload.length) return null;

          // Find the entry for the hovered point (by matching unique values)
          const chosen = payload.find(p => p.dataKey === 'actualWeight' && p.payload === hoveredWeightPoint) || payload[0];
          const data = chosen.payload;
          const color = chosen.color || '#333';
          const unit = selectedUnit.value === 'English' ? 'Pound' : 'gram';
          const lengthUnit = selectedUnit.value === 'English' ? 'Inch' : 'mm';
          const weightDiff = (data.actualWeight - data.standardWeight).toFixed(2);
          const percentDiff = ((data.actualWeight / data.standardWeight - 1) * 100).toFixed(1);

          return (
            <div className="custom-tooltip" style={{
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '12px',
              fontSize: isMobile ? '12px' : '14px',
              minWidth: '220px'
            }}>
              <div style={{
                borderBottom: '2px solid ' + color,
                marginBottom: '8px',
                paddingBottom: '4px'
              }}>
                <p style={{ margin: 0, fontWeight: 'bold', color }}>
                  {data.species}
                  {data.isStocking ? " · Stocking" : ""}
                </p>
              </div>
              <div style={{ display: 'grid', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>Length:</strong>
                  <span>{data.length.toFixed(2)} {lengthUnit}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>Actual Weight:</strong>
                  <span style={{ fontWeight: 'bold' }}>{data.actualWeight.toFixed(2)} {unit}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>Standard Weight:</strong>
                  <span>{data.standardWeight.toFixed(2)} {unit}</span>
                </div>
                <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '3px', fontSize: isMobile ? '11px' : '13px' }}>
                  <div style={{ color: (data.actualWeight - data.standardWeight) >= 0 ? '#27ae60' : '#c0392b', marginBottom: '4px' }}>
                    <strong>Difference:</strong> {weightDiff > 0 ? '+' : ''}{weightDiff} {unit}
                  </div>
                  <div style={{ color: ((data.actualWeight / data.standardWeight - 1) * 100) >= 0 ? '#27ae60' : '#c0392b' }}>
                    <strong>Percentage:</strong> {percentDiff > 0 ? '+' : ''}{percentDiff}%
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
      <Legend
        layout="horizontal"
        verticalAlign="top"
        align="center"
        wrapperStyle={{
          paddingBottom: isMobile ? 20 : 40,
          fontSize: isMobile ? 12 : 14,
        }}
      />
      {uniqueSpeciesWeight.map((species) => {
          const { fill: speciesColor, shape: scatterShape } =
            getWeightLengthRechartsScatterProps(species, uniqueSpeciesWeight);
          const all = graphData.filter((d) => d.species === species);
          const samp = all.filter((d) => !d.isStocking);
          const stock = all.filter((d) => d.isStocking);
          return (
            <React.Fragment key={species}>
              {samp.length > 0 && (
                <Scatter
                  name={`${species} Actual (${samp.length})`}
                  data={samp}
                  dataKey="actualWeight"
                  fill={speciesColor}
                  shape={scatterShape}
                  fillOpacity={0.8}
                  onMouseEnter={(o) => setHoveredWeightPoint(o?.payload || null)}
                  onMouseLeave={() => setHoveredWeightPoint(null)}
                />
              )}
              {stock.length > 0 && (
                <Scatter
                  name={`${species} Actual (stocking) (${stock.length})`}
                  data={stock}
                  dataKey="actualWeight"
                  fill={speciesColor}
                  shape={scatterShape}
                  fillOpacity={0.85}
                  onMouseEnter={(o) => setHoveredWeightPoint(o?.payload || null)}
                  onMouseLeave={() => setHoveredWeightPoint(null)}
                />
              )}
            </React.Fragment>
          );
        })}
      {fishTypesForStandard.map((fishType) => {
        let rows = graphData.filter(
          (d) => fishTypeLabelFromRow(d) === fishType && !d.isStocking
        );
        if (rows.length === 0) {
          rows = graphData.filter(
            (d) => fishTypeLabelFromRow(d) === fishType && d.isStocking
          );
        }
        if (rows.length === 0) return null;
        const sorted = [...rows].sort((a, b) => a.length - b.length);
        const { fill: lineColor } = getWeightLengthRechartsScatterProps(
          fishType,
          fishTypesForStandard
        );
        return (
          <Line
            key={`${fishType}-standard`}
            name={`${fishType} Standard`}
            data={sorted}
            dataKey="standardWeight"
            stroke={lineColor}
            strokeWidth={2}
            dot={false}
            type="monotone"
          />
        );
      })}
    </ComposedChart>
  </ResponsiveContainer>
);
}
